/**
 * Centralized badge color utilities for consistent styling across the application.
 * The admin is dark only.
 */

// Colour is for problems: red is filled, the other tones are outlines (see components/status-badge.tsx).
const GREEN = "bg-transparent text-green-400/90 border border-green-500/25 hover:bg-green-500/10";
const RED = "bg-red-500/15 text-red-300 border border-red-500/35 hover:bg-red-500/25";
const YELLOW = "bg-transparent text-amber-300/90 border border-amber-500/30 hover:bg-amber-500/10";
const ORANGE = "bg-transparent text-orange-300/90 border border-orange-500/30 hover:bg-orange-500/10";
const BLUE = "bg-transparent text-blue-300/90 border border-blue-500/30 hover:bg-blue-500/10";
const PURPLE = "bg-transparent text-purple-300/90 border border-purple-500/30 hover:bg-purple-500/10";
const GRAY = "bg-transparent text-muted-foreground border border-border hover:bg-muted/60";

/**
 * Get badge colors for status values (OK, PASS, FAIL, NOK, WARNING)
 */
export const getStatusBadgeColor = (status: string) => {
  const lowerStatus = status.toLowerCase();
  if (lowerStatus === "ok" || lowerStatus === "pass") {
    return GREEN;
  } else if (lowerStatus === "fail" || lowerStatus === "nok") {
    return RED;
  } else if (lowerStatus === "warning") {
    return YELLOW;
  }
  return GRAY;
};

/**
 * Get badge colors for confidence percentages
 */
export const getConfidenceBadgeColor = (percentage: number) => {
  if (percentage >= 90) return GREEN;
  if (percentage >= 70) return YELLOW;
  if (percentage >= 50) return ORANGE;
  return RED;
};

/**
 * Get badge colors for component counts
 */
export const getComponentCountBadgeColor = (count: number) => {
  if (count === 0) return GRAY;
  if (count <= 3) return GREEN;
  if (count <= 6) return YELLOW;
  return ORANGE;
};

/**
 * Get badge colors for pizza classification types
 */
export const getClassificationBadgeColor = (classification: string) => {
  switch (classification) {
    case "OK Pizza":
      return GREEN;
    case "Burnt Pizza":
      return RED;
    case "Uncooked Pizza":
      return ORANGE;
    case "No Pizza":
      return BLUE;
    default:
      return GRAY;
  }
};

/**
 * Get badge colors for component detection states
 */
export const getComponentDetectionBadgeColor = (state: 'found' | 'missing' | 'non_detectable' | 'extra' | 'unexpected') => {
  switch (state) {
    case 'found':
      return GREEN;
    case 'missing':
      return RED;
    case 'non_detectable':
      return YELLOW;
    case 'extra':
      return BLUE;
    case 'unexpected':
      return PURPLE;
    default:
      return GRAY;
  }
};

/**
 * Get badge colors for doneness evaluation (burnt, raw, ok)
 */
export const getDonenessBadgeColor = (type: 'burnt' | 'raw' | 'ok') => {
  switch (type) {
    case 'burnt':
      return RED;
    case 'raw':
      return ORANGE;
    case 'ok':
      return GREEN;
    default:
      return GRAY;
  }
};

/**
 * Get badge colors for detection states (detected, not detected)
 */
export const getDetectionBadgeColor = (detected: boolean) => {
  return detected ? GREEN : RED;
};

/**
 * Get badge colors for trend indicators in dashboard cards
 * Handles context-specific logic where "up" might be good or bad depending on the metric
 */
export const getTrendBadgeColor = (trend: 'up' | 'down', metricType: 'good-up' | 'good-down') => {
  const isGoodTrend = metricType === 'good-up' ? trend === 'up' : trend === 'down';

  return {
    background: isGoodTrend
      ? "bg-transparent border-green-500/25"
      : "bg-red-500/15 border-red-500/35",
    text: isGoodTrend
      ? "text-green-400/90 hover:bg-green-500/10"
      : "text-red-300 hover:bg-red-500/25"
  };
};

/**
 * Get metric type for different dashboard statistics
 * Determines whether "up" is good or bad for a given metric
 */
export const getMetricType = (title: string): 'good-up' | 'good-down' => {
  const goodDownMetrics = ['Burnt Pizzas', 'Missing Pizza Savers', 'Failed Quality Checks'];
  return goodDownMetrics.includes(title) ? 'good-down' : 'good-up';
};
